/**
 * The gate in front of the scheduler (SPEC §7, pedagogical invariants 1–3). **[DoD-1]**
 *
 * `schedule()` is called from exactly one place — `applyAttempt()` below — and only when
 * `advancesInterval()` is true. Everything else about an attempt is recorded and changes nothing
 * about `Memory`.
 *
 * This module is the mechanical form of three invariants:
 *
 *   1. Only a production-mode attempt satisfies an item's due date and advances its interval.
 *   2. A hinted attempt is logged as hinted and does not advance the interval.
 *   3. No multiple choice anywhere as a scored mode — enforced here by there being no recognition
 *      mode to admit. `productionVsRecognition.recognition` is structurally 0 (SPEC §9), and this
 *      file is why.
 *
 * Pure. No I/O, no clock, no randomness.
 */

import type { Attempt, Memory } from '../store/schema';
import type { ModeId } from '../state/gamestate';
import { DEFAULT_CONFIG, schedule, type Rating, type SchedulerConfig } from './fsrs';

/**
 * Every mode in which the learner *produces* Spanish rather than recognising it.
 *
 * `encoding` is the one exclusion, and it is not a recognition mode either: it is the forced
 * self-reference step after a second failure (invariant 5), where the learner writes a sentence
 * linking the item to his own life. It is deliberately unscored — the value is in the encoding,
 * and grading it would turn a memory aid into another thing to get wrong.
 */
const PRODUCTION_MODES: ReadonlySet<ModeId> = new Set<ModeId>([
  'tiles',
  'cloze',
  'conjugate',
  'conversion',
  'sentence',
  'speech',
]);

/**
 * Derived from the mode, never read from a field on the attempt.
 *
 * SPEC §7 sketches `attempt.isProductionMode`; a stored boolean can be written wrong, and would
 * then let a non-production attempt advance an interval with the audit log agreeing that it was
 * fine. The mode is what the learner actually did, so the mode decides.
 */
export function isProductionMode(mode: ModeId): boolean {
  return PRODUCTION_MODES.has(mode);
}

/** The fields the gate reads. Kept narrow so callers cannot smuggle in anything else. */
export type GateInput = Pick<Attempt, 'mode' | 'hinted' | 'isRepeatWithinRun'>;

/**
 * Whether this attempt may advance the item's FSRS interval.
 *
 * All three conditions, no exceptions and no override parameter. An override would be the first
 * thing reached for the moment a test went red, and DoD-1 is the criterion this whole design rests
 * on.
 */
export function advancesInterval(attempt: GateInput): boolean {
  return isProductionMode(attempt.mode) && !attempt.hinted && !attempt.isRepeatWithinRun;
}

/**
 * Grading outcome to FSRS rating (SPEC §7).
 *
 * No self-rating dialog and no "how did that feel?" — the rating is derived from what was typed.
 * A missing accent alone is `hard` rather than `again`: the retrieval succeeded, the orthography
 * did not, and treating it as a total failure would push a known word back to a one-day interval
 * over a diacritic.
 */
export function ratingFor(attempt: Pick<Attempt, 'correct' | 'nearMiss'>): Rating {
  if (!attempt.correct) return 'again';
  return attempt.nearMiss === 'missing-accent' || attempt.nearMiss === 'wrong-accent'
    ? 'hard'
    : 'good';
}

export interface ApplyResult {
  memory: Memory;
  /** Mirrored onto `Attempt.advancedInterval` so the invariant is auditable from the export. */
  advancedInterval: boolean;
  /** Null when the gate rejected the attempt — no rating was derived because none was used. */
  rating: Rating | null;
}

/**
 * The only sanctioned path from a graded attempt to a changed `Memory`.
 *
 * When the gate rejects the attempt, the returned memory is the **same object** that was passed
 * in — not a copy, not a rebuild. That makes "changed nothing" checkable by identity rather than
 * by deep comparison, so a future refactor that reconstructs the record cannot quietly satisfy the
 * test while introducing a field that drifts.
 */
export function applyAttempt(
  memory: Memory,
  attempt: GateInput & Pick<Attempt, 'correct' | 'nearMiss'>,
  now: number,
  cfg: SchedulerConfig = DEFAULT_CONFIG,
): ApplyResult {
  if (!advancesInterval(attempt)) {
    return { memory, advancedInterval: false, rating: null };
  }
  const rating = ratingFor(attempt);
  return { memory: schedule(memory, rating, now, cfg), advancedInterval: true, rating };
}
